<script setup lang="ts">
  import { INSTANCE as WindowUnloadManager } from '@/core/WindowUnloadManager'
  import { INSTANCE as moduleManager } from '@/core/ModuleManager'
  import ToastInfo from '@/components/home/ToastInfo.vue'
  import { onMounted, onScopeDispose, watch, ref, computed } from 'vue'
  import { changeDocumentPageTitle, isMobileTerminal } from '@core/Utils'
  import { throttle, noop, debounce, delay } from 'lodash'
  import { TimeConstants, Router } from '@core/Constants'
  import { INSTANCE as AuthorTokenManger } from '@/core/AuthorTokenManager'
  import { INSTANCE as UserInfoManager } from '@core/userInfo/UserInfoManager'
  import { HttpAuthorizationException } from '@core/Exceptions'
  import { isFrameReloginDisabled } from '@/DebugConfig'
  import { useSharedHeytapAccount } from '@core/compositions/HeytapAccount'
  import { useSharedActiveMenuItem } from '@/core/compositions/ActiveMenuItem'
  import { useDisableSlide } from '@core/compositions/DisableSlide'
  import { useI18n } from 'vue-i18n'
  import { MicroAppId } from '@core/application/ApplicationConstants'
  import { useSharedScreen } from '@core/compositions/Screen'

  const { t } = useI18n()
  const { VITE_BASE_PAGE_TITLE } = import.meta.env

  const EVENT_KEY_DOWN = 'keydown'
  const EVENT_POINTER_DOWN = 'pointerdown'
  const EVENT_MOUSE_WHEEL = 'wheel'
  const EVENT_POINTER_MOVE = 'pointermove'
  const globalEventLists = [EVENT_KEY_DOWN, EVENT_POINTER_DOWN, EVENT_MOUSE_WHEEL, EVENT_POINTER_MOVE]
  const PASSIVE_DETECTION_FAKE_EVENT = 'test'

  let isPassiveSupported = { passive: false }

  const { activeItem, isFileManager, activeAppId } = useSharedActiveMenuItem()
  const { isLargeScreen } = useSharedScreen()

  try {
    window.addEventListener(
      PASSIVE_DETECTION_FAKE_EVENT,
      noop,
      Object.defineProperty({}, 'passive', {
        get() {
          isPassiveSupported = { passive: true }
        },
      })
    )
  } catch (err) {
    // do nothing
  }

  const onUserInteractiveEvent = throttle(
    () => {
      AuthorTokenManger.refresh()
    },
    TimeConstants.USER_INTERACTIVE_EVENT_THROTTLING_IN_MS,
    {
      leading: true,
      trailing: true,
    }
  )

  const initModule = async () => {
    const isLogin = await UserInfoManager.getUserInfo()

    if (isLogin) {
      moduleManager.init()
    } else if (!isFrameReloginDisabled()) {
      throw new HttpAuthorizationException()
    }
  }

  const isMobileTerminalEnv = ref<boolean>()

  const initMobileTerminalEnv = async () => {
    isMobileTerminalEnv.value = await isMobileTerminal()

    if (isMobileTerminalEnv.value) {
      document.body.classList.add('isMobileTerminalEnv')
    }
  }

  onMounted(() => {
    useDisableSlide()
    initMobileTerminalEnv()

    for (const event of globalEventLists) {
      window.addEventListener(event, onUserInteractiveEvent, isPassiveSupported)
    }
    onScopeDispose(() => {
      for (const event of globalEventLists) {
        window.removeEventListener(event, onUserInteractiveEvent)
      }
    })
  })

  watch(activeItem, () => {
    if (activeItem.value) {
      const name = activeItem.value.name.trim()

      changeDocumentPageTitle(name || VITE_BASE_PAGE_TITLE)
    }
  })

  initModule()

  const { initAccountClientSdk } = useSharedHeytapAccount()

  initAccountClientSdk()

  window.onbeforeunload = function () {
    return WindowUnloadManager.referenceCount > 0 ? t('owork_base.leave_this_website') : null
  }
  const isNotStandardWebEnv = computed(() => {
    const appId = activeAppId.value as unknown as MicroAppId

    // 加入了fileManager中非file的文档判断，限制文档界面的宽度拉伸
    return (
      (isFileManager.value && appId !== MicroAppId.KINGSOFT_DOCUMENT && appId !== MicroAppId.QQ_DOCUMENT) ||
      appId === MicroAppId.DEV_INTEGRATION
    )
  })
  const isFirstSwitch = ref(true)

  // 临时修复措施，修复在小屏非文管状态下，切换设备，文管内容未加载的问题（如果先加载过文管一次，从文档等切换到h5文管时，微应用容器被更新，但是qiankun还是缓存了旧的dom容器，所以无法正确渲染微应用内容，所以临时刷新页面重新加载）
  watch(isNotStandardWebEnv, (newValue) => {
    if (newValue && isFirstSwitch.value) {
      isFirstSwitch.value = false

      return
    }

    if (newValue && !isLargeScreen.value) {
      delay(() => {
        location.reload()
      }, 1000)
    }
  })
</script>

<template>
  <a-config-provider :auto-insert-space-in-button="false">
    <div class="main" :class="{ isFileManager: isNotStandardWebEnv }">
      <ToastInfo />
      <RouterView />
    </div>
  </a-config-provider>
</template>
<style lang="stylus">
  .main
    main-rect()
    overflow-y hidden
    min-width main_min_width

  @import './components/styles/h5Adaptive/App.styl'
</style>
